import React, { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import { Flame, TrendingDown, TrendingUp, ShieldAlert, Leaf, ScanBarcode } from "lucide-react";
import SearchBar from "../components/SearchBar";
import MoleculeBackground from "../components/MoleculeBackground";
import SEO, { canonicalFor, titleFor, SITE_NAME, SITE_URL } from "../lib/seo";
import { getPreloaded, getTrending, getTrendingIngredients, slugify } from "../lib/api";

const ratingColor = (r) =>
  r === "red" ? "#FF3B3B" : r === "yellow" ? "#FFB800" : "#00C853";

const TrendingCard = ({ p, idx, testId }) => {
  const color = ratingColor(p.overallRating);
  return (
    <motion.div
      initial={{ opacity: 0, y: 12 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ delay: idx * 0.04 }}
      whileHover={{ y: -3 }}
      className="surface rounded-xl p-4 hover:border-[color:var(--lime)]/40 transition-colors"
    >
      <Link to={`/product/${p.slug}`} data-testid={testId} className="block">
        <div className="flex items-start justify-between gap-2">
          <div className="min-w-0">
            <div className="font-display font-semibold text-[color:var(--text)] truncate">{p.productName}</div>
            <div className="text-xs text-[color:var(--text-3)] truncate">{p.brand || p.category}</div>
          </div>
          <div className="flex flex-col items-end">
            <span className="font-mono text-[28px] leading-none font-bold" style={{ color }}>
              {p.overallScore}
            </span>
            <span className="font-mono text-[9px] tracking-[0.2em] uppercase text-[color:var(--text-3)]">/100</span>
          </div>
        </div>
        <div className="mt-3 text-xs text-[color:var(--text-2)] line-clamp-2 min-h-[32px]">
          {p.overallVerdict}
        </div>
      </Link>
    </motion.div>
  );
};

const PreloadedTile = ({ name, idx }) => (
  <motion.div
    initial={{ opacity: 0, scale: 0.96 }}
    animate={{ opacity: 1, scale: 1 }}
    transition={{ delay: idx * 0.015 }}
  >
    <Link
      to={`/product/${slugify(name)}?q=${encodeURIComponent(name)}`}
      data-testid={`preloaded-${idx}`}
      className="block surface rounded-lg px-3 py-2.5 text-sm text-[color:var(--text-2)] hover:text-[color:var(--lime)] hover:border-[color:var(--lime)]/30 transition-all"
    >
      {name}
    </Link>
  </motion.div>
);

export default function Landing() {
  const [preloaded, setPreloaded] = useState([]);
  const [trending, setTrending] = useState(null);
  const [flagged, setFlagged] = useState([]);

  useEffect(() => {
    getPreloaded().then(setPreloaded).catch(() => {});
    getTrending().then(setTrending).catch(() => {});
    getTrendingIngredients().then(setFlagged).catch(() => {});
  }, []);

  return (
    <div data-testid="landing-page" className="relative">
      <SEO
        title={titleFor()}
        description="Know what is inside your food. Search any product for an instant AI-powered safety breakdown — traffic-light ratings for every ingredient, daily-intake warnings and healthier alternatives. Free, no login."
        canonical={canonicalFor("/")}
        jsonLd={[
          {
            "@context": "https://schema.org",
            "@type": "Organization",
            name: SITE_NAME,
            url: SITE_URL,
            logo: `${SITE_URL}/icon-512.png`,
          },
          {
            "@context": "https://schema.org",
            "@type": "WebSite",
            name: SITE_NAME,
            url: SITE_URL,
            potentialAction: {
              "@type": "SearchAction",
              target: `${SITE_URL}/product/{search_term}`,
              "query-input": "required name=search_term",
            },
          },
        ]}
      />
      {/* HERO */}
      <section className="relative overflow-hidden">
        <MoleculeBackground />
        <div className="relative max-w-5xl mx-auto px-6 md:px-10 pt-16 md:pt-28 pb-20 text-center">
          <motion.div
            initial={{ opacity: 0, y: 8 }}
            animate={{ opacity: 1, y: 0 }}
            className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full border border-white/10 bg-white/[0.02] mb-6"
          >
            <span className="h-1.5 w-1.5 rounded-full bg-[color:var(--lime)] animate-pulse" />
            <span className="font-mono text-[10px] uppercase tracking-[0.25em] text-[color:var(--text-2)]">
              AI Food Forensics · Powered by Claude
            </span>
          </motion.div>

          <motion.h1
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.08 }}
            className="font-display text-4xl sm:text-5xl md:text-7xl font-bold leading-[1.02] tracking-tighter"
          >
            Do you really know<br />
            what's in your <span className="text-[color:var(--lime)]">food?</span>
          </motion.h1>

          <motion.p
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.25 }}
            className="mt-6 text-base md:text-lg text-[color:var(--text-2)] max-w-2xl mx-auto"
          >
            Know what is inside your food. Every ingredient. Every risk. Search any product and get an instant
            <span className="text-[color:var(--danger)]"> red</span>,
            <span className="text-[color:var(--warning)]"> amber</span>, and
            <span className="text-[color:var(--safe)]"> green</span> breakdown.
          </motion.p>

          <motion.div
            initial={{ opacity: 0, y: 12 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.4 }}
            className="mt-10 max-w-2xl mx-auto"
          >
            <SearchBar size="lg" />
            <Link
              to="/scan"
              data-testid="hero-scan-mobile"
              className="md:hidden mt-4 lime-btn rounded-xl w-full py-3 text-base font-semibold inline-flex items-center justify-center gap-2"
            >
              <ScanBarcode className="h-5 w-5" strokeWidth={2.5} /> Scan a barcode
            </Link>
          </motion.div>

          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.6 }}
            className="mt-6 flex flex-wrap items-center justify-center gap-x-5 gap-y-2 font-mono text-[10px] tracking-[0.2em] uppercase text-[color:var(--text-3)]"
          >
            <span>50+ Preloaded Products</span>
            <span className="opacity-40">·</span>
            <span>Live AI Analysis</span>
            <span className="opacity-40">·</span>
            <span>No Login Required</span>
          </motion.div>
        </div>
      </section>

      {/* MOST-FLAGGED ADDITIVES STRIP */}
      {flagged.length > 0 && (
        <section className="relative max-w-7xl mx-auto px-6 md:px-10 pb-10" data-testid="flagged-strip">
          <div className="surface rounded-2xl p-5 md:p-6 overflow-hidden">
            <div className="flex items-center justify-between mb-4 gap-4">
              <div className="flex items-center gap-2.5 min-w-0">
                <span className="relative flex h-2.5 w-2.5 flex-shrink-0">
                  <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-[#FF3B3B] opacity-60" />
                  <span className="relative inline-flex rounded-full h-2.5 w-2.5 bg-[#FF3B3B]" />
                </span>
                <div className="font-mono text-[10px] tracking-[0.25em] uppercase text-[color:var(--text-2)] truncate">
                  Most-flagged additives this week
                </div>
              </div>
              <Link
                to="/ingredients"
                data-testid="flagged-view-all"
                className="font-mono text-[10px] tracking-[0.2em] uppercase text-[color:var(--lime)] hover:opacity-80 whitespace-nowrap flex items-center gap-1"
              >
                View all <ShieldAlert className="h-3 w-3" />
              </Link>
            </div>
            <div className="flex gap-2.5 overflow-x-auto pb-1 -mx-1 px-1" style={{ scrollbarWidth: "thin" }}>
              {flagged.map((ing, i) => {
                const color = ratingColor(ing.rating);
                return (
                  <motion.div
                    key={ing.slug}
                    initial={{ opacity: 0, x: 14 }}
                    animate={{ opacity: 1, x: 0 }}
                    transition={{ delay: i * 0.05 }}
                  >
                    <Link
                      to={`/ingredient/${ing.slug}`}
                      data-testid={`flagged-${ing.slug}`}
                      className="group flex items-center gap-2.5 rounded-full border border-white/10 bg-white/[0.02] pl-3 pr-4 py-2 whitespace-nowrap hover:border-[color:var(--lime)]/40 transition-colors"
                    >
                      <span className="h-2 w-2 rounded-full flex-shrink-0" style={{ background: color, boxShadow: `0 0 10px ${color}` }} />
                      <span className="font-display text-sm font-medium text-[color:var(--text)] group-hover:text-[color:var(--lime)] transition-colors">
                        {ing.name}
                      </span>
                      <span className="font-mono text-[10px] text-[color:var(--text-3)]">
                        {ing.eNumber ? ing.eNumber : `${ing.count}×`}
                      </span>
                    </Link>
                  </motion.div>
                );
              })}
            </div>
          </div>
        </section>
      )}

      {/* QUICK PICKS */}
      <section className="relative max-w-7xl mx-auto px-6 md:px-10 pb-16">
        <div className="flex items-end justify-between mb-6">
          <div>
            <div className="font-mono text-[10px] tracking-[0.25em] uppercase text-[color:var(--text-3)] mb-1">
              Quick Picks
            </div>
            <h2 className="font-display text-2xl md:text-3xl font-semibold">Popular products</h2>
          </div>
        </div>
        <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-5 lg:grid-cols-6 gap-2.5">
          {preloaded.map((p, i) => (
            <PreloadedTile key={p} name={p} idx={i} />
          ))}
        </div>
      </section>

      {/* TRENDING */}
      {trending && (
        <section className="relative max-w-7xl mx-auto px-6 md:px-10 pb-24 space-y-14">
          {trending.mostSearched?.length > 0 && (
            <TrendingRow
              title="Most Analyzed"
              subtitle="What the community is checking right now"
              icon={<Flame className="h-4 w-4" />}
              products={trending.mostSearched}
              testBase="most-searched"
            />
          )}
          {trending.worstRated?.length > 0 && (
            <TrendingRow
              title="Worst Rated"
              subtitle="Products with the lowest health scores"
              icon={<TrendingDown className="h-4 w-4 text-[color:var(--danger)]" />}
              products={trending.worstRated}
              testBase="worst-rated"
            />
          )}
          {trending.bestRated?.length > 0 && (
            <TrendingRow
              title="Surprisingly Good"
              subtitle="Products with unexpectedly high scores"
              icon={<TrendingUp className="h-4 w-4 text-[color:var(--safe)]" />}
              products={trending.bestRated}
              testBase="best-rated"
            />
          )}
          {trending.mostControversial?.length > 0 && (
            <TrendingRow
              title="Most Controversial"
              subtitle="Highest count of red-flag ingredients"
              icon={<ShieldAlert className="h-4 w-4 text-[color:var(--warning)]" />}
              products={trending.mostControversial}
              testBase="most-controversial"
            />
          )}
        </section>
      )}

      {/* VALUE PROPS */}
      <section className="relative max-w-7xl mx-auto px-6 md:px-10 pb-24">
        <div className="grid md:grid-cols-3 gap-4">
          <ValueCard
            icon={<ShieldAlert className="h-5 w-5" />}
            title="Traffic-light simplicity"
            body="Every ingredient rated Red, Yellow, or Green at a glance — no chemistry degree required."
          />
          <ValueCard
            icon={<Leaf className="h-5 w-5" />}
            title="Healthier alternatives"
            body="When something's off, we suggest better picks on the spot so grocery runs stay easy."
          />
          <ValueCard
            icon={<Flame className="h-5 w-5" />}
            title="Daily intake warnings"
            body="See exactly how much of your daily sugar, salt, or fat limit a single serving uses."
          />
        </div>
      </section>
    </div>
  );
}

const TrendingRow = ({ title, subtitle, icon, products, testBase }) => (
  <div data-testid={`trending-${testBase}`}>
    <div className="flex items-end justify-between mb-5">
      <div>
        <div className="font-mono text-[10px] tracking-[0.25em] uppercase text-[color:var(--text-3)] flex items-center gap-2 mb-1">
          {icon} {title}
        </div>
        <h3 className="font-display text-xl md:text-2xl font-semibold">{subtitle}</h3>
      </div>
    </div>
    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
      {products.map((p, i) => (
        <TrendingCard key={p.slug} p={p} idx={i} testId={`${testBase}-card-${i}`} />
      ))}
    </div>
  </div>
);

const ValueCard = ({ icon, title, body }) => (
  <div className="surface rounded-xl p-5">
    <div className="h-9 w-9 rounded-lg bg-[color:var(--lime)]/10 text-[color:var(--lime)] flex items-center justify-center mb-4">
      {icon}
    </div>
    <div className="font-display font-semibold text-lg mb-1">{title}</div>
    <div className="text-sm text-[color:var(--text-2)] leading-relaxed">{body}</div>
  </div>
);
